import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Quote, Sparkles, Zap, ShieldCheck, PenTool, TrendingUp, Clock, Layers } from 'lucide-react';
import SEO from '../SEO';
import V2Hero from './V2Hero';
import Section from './Section';
import FAQSchema from '../FAQSchema';
import { CTABlock } from './CTAButton';
import AmazonBookProof from './AmazonBookProof';
import PricingEstimator from './PricingEstimator';
import AudiobookEstimator from './AudiobookEstimator';
import PodcastEstimator from './PodcastEstimator';

// Rotating premium icon set for deliverables - varied iconography instead of ticks.
const DELIVERABLE_ICONS = [Sparkles, Zap, ShieldCheck, PenTool, TrendingUp, Clock, Layers];

/**
 * Reusable service landing page template used by every /podcast-production,
 * /podcast-editing, /podcast-launch, /podcast-to-youtube, /audiobook-production
 * and /self-publishing page.
 *
 * Renders the exact section order specified in the Sep 2026 rebuild spec:
 *   1. Hero (single H1)
 *   2. What you get
 *   3. How it works
 *   4. Timeline / turnaround
 *   5. Pricing guidance
 *   6. Why Audio & Co (proof)
 *   7. FAQs (answers in HTML on load)
 *   8. CTA repeat
 *
 * Every service page passes the same shape via `data`, so we only maintain
 * a single template. All FAQs are rendered as plain <details>/<summary>
 * so the answer text sits in HTML on first paint (Googlebot + Perplexity
 * can quote it verbatim without needing to click).
 */
export default function ServicePage({ data }) {
  // Accept both snake_case (Seo.js legacy) and camelCase keys.
  const d = data || {};
  const slug = d.slug;
  const seoTitle = d.seoTitle || d.seo_title;
  const seoDescription = d.seoDescription || d.seo_description;
  const heroKicker = d.heroKicker || d.kicker;
  const h1 = d.h1;
  const subhead = d.subhead;
  const heroImage = d.heroImage;
  const deliverables = d.deliverables;
  const process = d.process || d.how_it_works;
  const turnaround = d.turnaround || d.timeline;
  const pricingHeadline = d.pricingHeadline;
  const pricingLine = d.pricingLine || d.pricing_note;
  const pricingSub = d.pricingSub;
  const whyBullets = d.whyBullets || d.why_us;
  // Per-page real client anchor. Defaults to Juliette Karaman's verbatim
  // quote (sourced from /results Results.js line 16) unless a page opts
  // into a different client via `proofQuote` + `proofAuthor`.
  const proofQuote = d.proofQuote || 'Less than 1 month from first speaking it was launched. Within 2 months we are ranking in the top 10% worldwide.';
  const proofAuthor = d.proofAuthor || 'Juliette Karaman \u00b7 The Scrumptious Woman';
  const faqs = d.faqs;
  const relatedLinks = d.relatedLinks
    || [...(d.related_services || []), ...(d.related_blog || [])].map(
        (l) => ({ to: l.to, label: l.label })
    );

  // Normalise legacy URL strings into the shape V2Hero expects.
  const heroImageMeta = typeof heroImage === 'string'
    ? { src: heroImage, alt: h1 }
    : heroImage;

  return (
    <div className="bg-white">
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={`https://audioandco.com/${slug}`}
      />
      <FAQSchema faqs={(faqs || []).map((f) => ({ question: f.q, answer: f.a }))} />

      {/* 1. HERO — exactly one H1 */}
      <V2Hero image={heroImageMeta} testId={`${slug}-hero`}>
        <div className="max-w-3xl">
          {heroKicker && (
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">{heroKicker}</p>
          )}
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            {h1}
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">{subhead}</p>
          <div className="mt-8 flex flex-wrap gap-6 items-start">
            <CTABlock size="lg" testIdPrefix={`${slug}-hero-cta`} />
          </div>
        </div>
      </V2Hero>

      {/* 2. WHAT YOU GET — premium dark section with cascading spotlight */}
      <Section bg="dark" testId={`${slug}-deliverables`} innerClassName="!py-20 relative">
        <div className="absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full bg-[#A7468A]/20 blur-3xl pointer-events-none v2-blob" aria-hidden="true" />
        <div className="absolute -bottom-24 -right-16 w-[360px] h-[360px] rounded-full bg-[#BC516F]/15 blur-3xl pointer-events-none v2-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />
        <div className="relative max-w-3xl">
          <p className="text-[#E0A8B8] font-semibold text-sm uppercase tracking-[0.2em] flex items-center gap-3">
            <span className="w-8 h-px bg-[#E0A8B8]/60" />
            What you get
          </p>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05]">
            Every deliverable, <span className="v2-shimmer">handled for you</span>.
          </h2>
        </div>
        <div className="relative mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(deliverables || []).map((raw, i) => {
            // Accept either a plain string or a rich {title, body} object.
            const d = typeof raw === 'string' ? { title: raw } : (raw || {});
            const Icon = DELIVERABLE_ICONS[i % DELIVERABLE_ICONS.length];
            const num = String(i + 1).padStart(2, '0');
            return (
              <div
                key={i}
                className="v2-cascade group relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[16px] p-6 sm:p-7 flex flex-col overflow-hidden"
                style={{ '--card-index': i }}
                data-testid={`${slug}-deliverable-${i}`}
              >
                <div className="absolute top-4 right-5 v2-serif-accent text-white/25 text-3xl select-none pointer-events-none">
                  {num}
                </div>
                <div
                  className="v2-cascade-orb w-12 h-12 rounded-[12px] flex items-center justify-center shadow-[0_10px_24px_-6px_rgba(188,81,111,0.5)]"
                  style={{ background: 'linear-gradient(135deg, #BC516F 0%, #A7468A 55%, #81162A 100%)' }}
                >
                  <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="font-display font-semibold text-white text-lg leading-tight mt-5">{d.title}</h3>
                {d.body && (
                  <p className="text-white/65 mt-2 leading-relaxed text-[15px] flex-1">{d.body}</p>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* 3. HOW IT WORKS */}
      <Section bg="bg" testId={`${slug}-process`}>
        <div className="max-w-4xl">
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">How it works</h2>
          <ol className="mt-6 grid gap-4 sm:grid-cols-3">
            {(process || []).map((p, i) => (
              <li key={i} className="bg-white rounded-[14px] p-5 border border-[#E2E8F0]">
                <div className="text-[#BC516F] font-display font-bold text-2xl">{i + 1}.</div>
                <h3 className="font-display font-semibold text-[#1E2A44] text-lg mt-1">{p.title}</h3>
                <p className="text-[#475569] mt-2 leading-relaxed">{p.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      {/* 4. TIMELINE / TURNAROUND */}
      {turnaround && (
        <Section bg="white" testId={`${slug}-turnaround`}>
          <div className="max-w-3xl">
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">Timeline &amp; turnaround</h2>
            <p className="text-[#475569] text-lg mt-5 leading-relaxed">{turnaround}</p>
          </div>
        </Section>
      )}

      {/* 5. PRICING GUIDANCE */}
      <Section bg="bg" testId={`${slug}-pricing`}>
        <div className="max-w-3xl">
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">{pricingHeadline || 'Pricing guidance'}</h2>
          <p className="text-[#1E2A44] text-2xl font-display font-semibold mt-4">{pricingLine}</p>
          {pricingSub && <p className="text-[#475569] mt-3 leading-relaxed">{pricingSub}</p>}
          {d.showEstimator && (
            <div className="mt-8">
              <PricingEstimator testId={`${slug}-estimator`} />
            </div>
          )}
          {d.showAudiobookEstimator && (
            <div className="mt-8">
              <AudiobookEstimator testId={`${slug}-estimator`} />
            </div>
          )}
          {d.showPodcastEstimator && (
            <div className="mt-8">
              <PodcastEstimator testId={`${slug}-estimator`} />
            </div>
          )}
          <div className="mt-6">
            <CTABlock size="lg" testIdPrefix={`${slug}-pricing-cta`} />
          </div>
        </div>
      </Section>

      {/* 6. WHY AUDIO & CO */}
      <Section bg="white" testId={`${slug}-why`}>
        <div className="max-w-3xl">
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">Why Audio &amp; Co</h2>
          <ul className="mt-6 space-y-3">
            {(whyBullets || []).map((w, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#22C55E] mt-1 flex-shrink-0" />
                <span className="text-[#1E2A44] text-lg">{w}</span>
              </li>
            ))}
          </ul>
          {/* Real client anchor — verbatim quote per page. Default sits
              on Juliette Karaman (see hook above); pages using a different
              service (e.g. self-publishing) override via `proofQuote` and
              `proofAuthor` in their DATA object. */}
          <figure className="mt-8 border-l-4 border-[#BC516F] bg-[#F8FAFC] rounded-r-[10px] p-5" data-testid={`${slug}-proof`}>
            <Quote className="w-5 h-5 text-[#BC516F] mb-2" />
            <blockquote className="text-[#1E2A44] font-display font-semibold text-lg leading-snug">
              &ldquo;{proofQuote}&rdquo;
            </blockquote>
            <figcaption className="mt-2 text-sm text-[#475569]">{proofAuthor}</figcaption>
          </figure>
          {d.proofBooks && d.proofBooks.length > 0 && (
            <AmazonBookProof books={d.proofBooks} testId={`${slug}-books`} />
          )}
        </div>
      </Section>

      {/* 7. FAQs — plain <details> so answers ship in HTML on load */}
      {(faqs || []).length > 0 && (
        <Section bg="bg" testId={`${slug}-faqs`}>
          <div className="max-w-3xl">
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">Frequently asked questions</h2>
            <div className="mt-6 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="bg-white rounded-[14px] p-5 border border-[#E2E8F0]" open={i === 0}>
                  <summary className="font-display font-semibold text-[#1E2A44] text-lg cursor-pointer">{f.q}</summary>
                  <p className="text-[#475569] mt-3 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* 8. CTA REPEAT + related internal links */}
      <Section bg="white" testId={`${slug}-final-cta`}>
        <div className="max-w-3xl text-center">
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">Ready to get started?</h2>
          <p className="text-[#475569] text-lg mt-4">30 minutes. No pressure, no commitment.</p>
          <div className="mt-6 flex justify-center">
            <CTABlock size="lg" align="center" testIdPrefix={`${slug}-final-cta-btn`} />
          </div>
          {relatedLinks && relatedLinks.length > 0 && (
            <div className="mt-10 pt-8 border-t border-[#E2E8F0]">
              <p className="text-sm text-[#64748B] uppercase tracking-widest font-semibold mb-3">Related</p>
              <div className="flex flex-wrap justify-center gap-3">
                {relatedLinks.map((r, i) => (
                  <Link
                    key={i}
                    to={r.to}
                    className="text-[#1E2A44] hover:text-[#BC516F] font-semibold underline decoration-2 underline-offset-4"
                  >
                    {r.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
